/* eslint-disable @next/next/no-img-element */
'use client';
import { useRouter } from 'next/router';
import React, { useContext, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Password } from 'primereact/password';
import { LayoutContext } from '../../layout/context/layoutcontext';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Toast } from 'primereact/toast';
import { API_GET_CONFIG_USER, API_GET_USERMENU, API_LOGIN, API_MODULOS_ACTIVOS } from '@/config/config';
import axios from 'axios';
import { saveToLocalStorage } from '@/utils/utils';
import PasswordForm from './PasswordForm';
import handleError from '@/utils/handleError';
import { LayoutState } from '@/types';

const DEFAULT_LAYOUT_CONFIG = {
    scale: 14,
    theme: "lara-light-indigo",
    ripple: false,
    menuMode: "static",
    inputStyle: "outlined",
    colorScheme: "light"
};

// Login component
const Login: React.FC = () => {
    const [username, setUser] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [userId, setUserId] = useState<number | null>(null);
    const [showPasswordDialog, setShowPasswordDialog] = useState<boolean>(false);
    const [hasExpiredPassword, setHasExpiredPassword] = useState<boolean>(false);
    const { layoutConfig, setLayoutState } = useContext(LayoutContext);
    const router = useRouter();
    const containerClassName = classNames('surface-ground flex align-items-center justify-content-center min-h-screen min-w-screen overflow-hidden', { 'p-input-filled': layoutConfig.inputStyle === 'filled' });
    const toast = useRef<Toast>(null);

    const onConfigButtonClick = () => {
              setLayoutState((prevState: LayoutState) => ({ ...prevState, configSidebarVisible: true }));
          };

    const fetchLogin = async () => {
        try {
            // Primeramente obtenemos el login
            const fetchedLogin: any = await axios.post(`${API_LOGIN}`, { username, password });
            const data = fetchedLogin.data;
            if (data.error === "User has no password") {
                setUserId(data.ulserid);
                setShowPasswordDialog(true);
                setHasExpiredPassword(false); // No tiene contraseña, así que no necesitamos la antigua
                return;
            }

            if (data.error === "User has expired") {
                setUserId(data.ulserid);
                setShowPasswordDialog(true);
                setHasExpiredPassword(true); // Contraseña caducada, requerimos la antigua
                return;
            }

            // Guardamos el usuario en localStorage
            saveToLocalStorage('user', data.user);

            // Si la respuesta del login contiene un token
            if (data && data.token) {
                // Usamos await para garantizar que la configuración del usuario se cargue después de recibir el token
                try {
                    const mergedLayoutConfig = await axios.get(`${API_GET_CONFIG_USER}`);

                    if (mergedLayoutConfig && mergedLayoutConfig.data.jssettings) {
                        saveToLocalStorage('layout-config', JSON.stringify(mergedLayoutConfig.data.jssettings));
                    } else {
                        saveToLocalStorage('layout-config', JSON.stringify(DEFAULT_LAYOUT_CONFIG));
                    }
                } catch (error: any) {
                    if (error.response && error.response.status === 401) {
                        console.warn('No se pudo cargar la configuracion del usuario (401). Se usara la configuracion por defecto.');
                    } else {
                        console.error('Error al obtener la configuración de diseño:', error);
                    }
                    saveToLocalStorage('layout-config', JSON.stringify(DEFAULT_LAYOUT_CONFIG));
                }

                // Después de configurar el usuario, cargamos el menú
                try {
                    const menuUser = await axios.get(`${API_GET_USERMENU}`);
                    const menuData = menuUser.data;
                    menuData.push({
                        label: 'Usuario',
                        items: [{
                            label: 'Perfl',
                            icon: 'pi pi-user-edit',
                            to: '/administracion/seguridad/perfilusuario'
                        },
                        { label: 'Logout', icon: 'pi pi-sign-out', to: '/logout' }]
                    });
                    saveToLocalStorage('menu', JSON.stringify(menuData));
                } catch (error) {
                    console.error('Error al obtener el menú del usuario:', error);
                }

                // Guardamos modulos activos en localStorage
                try {
                    const modulosActivos = await axios.get(`${API_MODULOS_ACTIVOS}`);
                    saveToLocalStorage('modulos-activos', JSON.stringify(modulosActivos.data.modulos_activos));
                    saveToLocalStorage('parametros-activos', JSON.stringify(modulosActivos.data.parametros));
                } catch (error) {
                    console.error('Error al obtener los módulos activos:', error);
                }

                // Finalmente, redirigimos al usuario a la página principal
                await router.replace('/pericles');
            } else {
                // Si el login no fue correcto, mostramos un error
                if (toast.current) {
                    (toast.current as Toast).show({ severity: 'error', summary: 'Error', detail: 'Inicio de sesión incorrecto' });
                }
            }
        } catch (error) {
            console.error('Error al iniciar sesión:', error);
            handleError(error, toast);
        }
    };



    return (
        <div className={containerClassName} style={{
            background: `linear-gradient(180deg, var(--primary-color) 10%, rgba(33, 150, 243, 0) 30%), 
                    linear-gradient(rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)),
                    url('/layout/images/logo-dark.webp') no-repeat center center / cover`}}>
            <Toast ref={toast} />
            <div className="flex flex-column align-items-center justify-content-center">
                {/* <img src={`/layout/images/logo-${layoutConfig.colorScheme === 'light' ? 'dark' : 'white'}.svg`} alt="Sakai logo" className="mb-5 w-6rem flex-shrink-0" /> */}
                <div
                    style={{
                        borderRadius: '56px',
                        padding: '0.3rem',
                        background: 'linear-gradient(180deg, var(--primary-color) 10%, rgba(33, 150, 243, 0) 30%)'
                    }}
                >
                    <div className="w-full surface-card py-8 px-5 sm:px-8" style={{ borderRadius: '53px', backgroundColor: '#ffffffb0 !important' }}>
                        <div className="text-center mb-5">
                            <img src={`/layout/images/logo-${layoutConfig.colorScheme === 'light' ? 'dark' : 'white'}.webp`}
                                alt="Pericles"
                                className="mb-3 w-6rem flex-shrink-0" />
                            {/* <img src="/demo/images/login/avatar.png" alt="Image" height="50" className="mb-3" /> */}
                            <div className="text-900 text-3xl font-medium mb-3">Bienvenido a Pericles!</div>
                            <span className="text-600 font-medium">Accede para continuar</span>
                        </div>

                        <div>
                            <label htmlFor="email1" className="block text-900 text-xl font-medium mb-2">
                                Usuario
                            </label>
                            <InputText id="email1" type="text"
                                value={username} onChange={(e) => setUser(e.target.value)}
                                placeholder="Usuario" className="w-full md:w-30rem mb-5" style={{ padding: '1rem' }} />

                            <label htmlFor="password1" className="block text-900 font-medium text-xl mb-2">
                                Contraseña
                            </label>
                            <Password inputId="password1" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña" toggleMask className="w-full mb-5" inputClassName="w-full p-3 md:w-30rem"></Password>

                            <div className="flex align-items-center justify-content-between mb-5 gap-5">
                                {/* <div className="flex align-items-center">
                                    <Checkbox inputId="rememberme1" checked={checked} onChange={(e) => setChecked(e.checked ?? false)} className="mr-2"></Checkbox>
                                    <label htmlFor="rememberme1">Remember me</label>
                                </div> */}
                                <a className="font-medium no-underline ml-2 text-right cursor-pointer" style={{ color: 'var(--primary-color)' }}>
                                    Olvidaste la contraseña?
                                </a>
                            </div>
                            <Button label="Acceder" className="w-full p-3 text-xl" onClick={fetchLogin}></Button>
                        </div>
                    </div>
                </div>
            </div>
            <PasswordForm
                visible={showPasswordDialog}
                userId={userId}
                onClose={() => setShowPasswordDialog(false)}
                onSuccess={() => {
                    setShowPasswordDialog(false);
                    toast.current?.show({ severity: 'success', summary: 'Éxito', detail: 'Contraseña creada con éxito.' });
                }}
                hasExpiredPassword={hasExpiredPassword}
            />
        </div>
    );
};

export default Login;
